import { createTheme, ThemeOptions } from "@mui/material/styles"
import colors from "./colors"

// MUI v7: createBreakpoints removed - create temporary theme to access breakpoints
const tempTheme = createTheme()
const breakpoints = tempTheme.breakpoints

const muiThemeOptions: ThemeOptions = {
  spacing: 3,
  typography: {
    // Font loaded via next/font in layout.tsx as CSS variable
    fontFamily: "var(--font-raleway), sans-serif",
    h1: {
      fontSize: "23px",
      fontWeight: "normal",
      color: colors.darkBlue,
      [breakpoints.up("md")]: {
        fontSize: "36px",
      },
    },
    h2: {
      fontSize: "36px",
      fontWeight: "bold",
      color: colors.darkBlue,
      [breakpoints.up("md")]: {
        fontSize: "36px",
      },
    },
    h3: {
      fontSize: "28px",
      fontWeight: "bold",
      color: colors.darkRoyalBlue,
      [breakpoints.up("md")]: {
        fontSize: "36px",
      },
    },
    body1: {
      // default font - match production 14px
      fontSize: "14px",
    },
    body2: {
      fontSize: "0.875rem",
    },
  },
  palette: {
    background: {
      default: colors.white,
    },
    primary: {
      main: colors.darkBlue,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.white,
      contrastText: colors.darkBlue,
    },
    text: {
      primary: colors.darkBlue,
      secondary: colors.white,
    },
    error: {
      main: "#b50303",
    },
  },
  components: {
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        variant: "outlined",
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          color: colors.darkBlue,
        },
        outlined: {
          legend: {
            fontWeight: "bold",
            fontSize: "14px",
            [breakpoints.up("md")]: {
              fontSize: "16px",
            },
          },
          "&$shrink": {
            transform: "translate(15px, -8px) scale(1)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        notched: true,
      },
      styleOverrides: {
        notchedOutline: {
          fontSize: "1em",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: "md" as const,
      },
      styleOverrides: {
        paperWidthSm: {
          maxWidth: "calc(100% - 30px)",
        },
        paperScrollPaper: {
          maxHeight: "calc(100% - 90px)",
          margin: "45px 15px",
          overflowY: "initial",
        },
        paper: {
          width: "100%",
          margin: "32px 6px",
        },
        paperScrollBody: {
          overflow: "visible",
          margin: "50px 6px",
          [breakpoints.down("sm")]: {
            width: "calc(100% - 12px)",
            maxWidth: "100% !important",
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          borderRadius: "5px",
          boxShadow: "0 5px 99px 0 rgba(9, 42, 70, 0.6)",
          border: "solid 1px #707070",
          backgroundColor: colors.white,
        },
      },
    },
    MuiImageListItemBar: {
      styleOverrides: {
        titleWrap: { flexGrow: 0 },
        title: {
          fontSize: "1.25rem",
          [breakpoints.up("md")]: {
            fontSize: "1.5rem",
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        colorPrimary: {
          color: colors.darkBlue,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.darkBlue,
          "&.Mui-checked": {
            color: colors.darkBlue,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableTouchRipple: true,
        disableFocusRipple: true,
        disableRipple: true,
      },
      styleOverrides: {
        contained: {
          color: colors.darkBlue,
          backgroundColor: colors.white,
          border: `1px solid ${colors.white}`,
          outline: "none",
          boxShadow: "none",
          "&:hover,&:focus": {
            color: colors.white,
            backgroundColor: colors.darkBlue,
            boxShadow: "none",
          },
        },
        outlined: {
          color: colors.darkBlue,
          backgroundColor: colors.white,
          outline: "none",
          boxShadow: "none",
          border: `2px solid ${colors.hotPink}`,
          "&:hover,&:focus": {
            color: colors.white,
            backgroundColor: colors.darkBlue,
            border: "none",
            boxShadow: "none",
          },
        },
        outlinedSecondary: {
          color: colors.white,
          backgroundColor: "transparent",
          border: `2px solid ${colors.white}`,
          "&:hover": {
            color: colors.white,
            backgroundColor: "transparent",
            border: "none",
          },
        },
        sizeSmall: {
          fontSize: "14px",
          [breakpoints.down("sm")]: {
            padding: "5px 0",
          },
        },
        root: {
          borderRadius: 25,
          fontWeight: 600,
          fontSize: "24px", // Match production mobile button size
          textTransform: "none",
          boxShadow: "none",
          [breakpoints.down("sm")]: {
            width: "auto",
            padding: "8px 24px",
          },
          [breakpoints.up("md")]: {
            padding: "10px 60px",
            fontSize: "20px",
          },
          "&:hover": {
            boxShadow: "none",
          },
        },
        startIcon: {
          marginLeft: "-20px",
          fontSize: "1.5em",
          marginTop: "-5px",
          marginBottom: "-5px",
        },
      },
    },
    MuiAvatar: {
      defaultProps: {
        // @ts-ignore - color prop exists on Avatar
        color: colors.hotPink,
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(1, 21, 46, 0.55)",
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingRight: "15px",
          paddingLeft: "15px",
          [breakpoints.up("sm")]: {
            paddingRight: "15px",
            paddingLeft: "15px",
          },
        },
      },
    },
  },
}

const theme = {
  ...createTheme(muiThemeOptions),
  colors,
}

export default theme

export type Theme = typeof theme
